import { useApiDealsV1SellerTransactionHistoryRetrieve, useCurrentUser } from 'api';
import { Alert, Box, DataCard, formatNumber, Stack, Typography } from 'ui';
import { Campaign, Celebration, Restore } from 'ui/icons';

export const TransactionHistoryDataCard = ({ sellerId }: { sellerId: number }) => {
  const { data: user, isLoading: isUserLoading } = useCurrentUser();
  const { data: totalTransactionHistory, isLoading: isTotalTransactionHistoryLoading } =
    useApiDealsV1SellerTransactionHistoryRetrieve(sellerId, undefined, {
      query: {
        enabled: Boolean(user),
      },
    });
  const { data: buyerTransactionHistory, isLoading: isBuyerTransactionHistoryLoading } =
    useApiDealsV1SellerTransactionHistoryRetrieve(
      sellerId,
      {
        buyer_id: user?.id,
      },
      {
        query: {
          enabled: Boolean(user),
        },
      }
    );

  const hasTransactions = Boolean(totalTransactionHistory);

  if (!hasTransactions) {
    return null;
  }

  const isLoading = isUserLoading || isTotalTransactionHistoryLoading || isBuyerTransactionHistoryLoading;

  const hasBuyerTransactions =
    Boolean(buyerTransactionHistory?.loads_count) || Boolean(buyerTransactionHistory?.total_tonnes);

  const totalTransactions = {
    loads: totalTransactionHistory?.loads_count ?? 0,
    tonnes: totalTransactionHistory?.total_tonnes ? Number(totalTransactionHistory?.total_tonnes) : 0,
  };

  const buyerTransactions = {
    loads: buyerTransactionHistory?.loads_count ?? 0,
    tonnes: buyerTransactionHistory?.total_tonnes ? Number(buyerTransactionHistory?.total_tonnes) : 0,
  };

  return (
    <DataCard
      isLoading={isLoading}
      slotProps={{
        header: {
          avatar: <Restore />,
          title: 'Transaction History',
        },
      }}
    >
      <Stack gap={2} width="100%">
        {hasBuyerTransactions ? (
          <BuyerTransactions {...buyerTransactions} />
        ) : (
          <Alert severity="info" icon={<Campaign />}>
            New supplier! You haven&apos;t purchased from them before.
          </Alert>
        )}
        <TotalTransactions {...totalTransactions} />
      </Stack>
    </DataCard>
  );
};

const TotalTransactions = ({ tonnes, loads }: { tonnes: number; loads: number }) => {
  return (
    <Stack direction="column" gap={2}>
      <Typography variant="body2" fontWeight="bold">
        Transactions between this supplier and other buyers in the past 6 months.
      </Typography>
      <Stack direction="row" justifyContent="center">
        <DataCard.Item
          variant="emphasis"
          title={formatNumber(tonnes, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          })}
          data-testid="total-tonnes"
        >
          Metric tonnes
        </DataCard.Item>
        <DataCard.Item
          variant="emphasis"
          title={formatNumber(loads, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          })}
          data-testid="total-loads"
        >
          Loads
        </DataCard.Item>
      </Stack>
    </Stack>
  );
};

const BuyerTransactions = ({ tonnes, loads }: { tonnes: number; loads: number }) => {
  return (
    <Box
      sx={{
        backgroundColor: 'primary.light',
        borderRadius: '8px',
        color: 'primary.main',
      }}
    >
      <Alert severity="info" icon={<Celebration />}>
        You&apos;ve purchased from this supplier before!
      </Alert>
      <Stack direction="row" justifyContent="center">
        <DataCard.Item
          variant="emphasis"
          title={formatNumber(tonnes, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          })}
          data-testid="buyer-tonnes"
        >
          Metric tonnes
        </DataCard.Item>
        <DataCard.Item
          variant="emphasis"
          title={formatNumber(loads, {
            maximumFractionDigits: 0,
            minimumFractionDigits: 0,
          })}
          data-testid="buyer-loads"
        >
          Loads
        </DataCard.Item>
      </Stack>
    </Box>
  );
};
